/**
 * Session State Service
 * 
 * Manages the entire session lifecycle with a state machine pattern.
 * Provides a single source of truth for session state across the application.
 * 
 * Issue: #202
 */

import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'
import { map, distinctUntilChanged } from 'rxjs/operators'

import { log, warn, error } from '@/util/logger'

export enum SessionState {
  Unknown = 'unknown',
  Authenticated = 'authenticated',
  Refreshing = 'refreshing',
  Error = 'error',
  Expired = 'expired',
  LoggedOut = 'logged_out'
}

export interface StateTransition {
  from: SessionState
  to: SessionState
  timestamp: number
  context?: any
}

/**
 * Centralized session state management service.
 * Tracks session lifecycle and provides observables for state changes.
 */
@Injectable({ providedIn: 'root' })
export class SessionStateService {
  private _state = new BehaviorSubject<SessionState>(SessionState.Unknown)
  private _transitionHistory: StateTransition[] = []
  private readonly _maxHistorySize = 50

  public readonly state$ = this._state.asObservable()

  // Derived observables for convenience
  public readonly isAuthenticated$ = this.state$.pipe(
    map(s => s === SessionState.Authenticated),
    distinctUntilChanged()
  )

  public readonly isRefreshing$ = this.state$.pipe(
    map(s => s === SessionState.Refreshing),
    distinctUntilChanged()
  )

  public readonly hasError$ = this.state$.pipe(
    map(s => s === SessionState.Error),
    distinctUntilChanged()
  )

  public readonly isLoggedOut$ = this.state$.pipe(
    map(s => s === SessionState.LoggedOut),
    distinctUntilChanged()
  )

  constructor() {
    log('[SessionState] Service initialized, state:', this._state.value)
  }

  /**
   * Get current state synchronously
   */
  get currentState(): SessionState {
    return this._state.value
  }

  /**
   * Get transition history for debugging
   */
  get transitionHistory(): StateTransition[] {
    return [...this._transitionHistory]
  }

  /**
   * Transition to a new state with validation
   */
  transitionTo(newState: SessionState, context?: any): void {
    const currentState = this._state.value

    if (this._isValidTransition(currentState, newState)) {
      const transition: StateTransition = {
        from: currentState,
        to: newState,
        timestamp: Date.now(),
        context
      }

      this._transitionHistory.push(transition)
      if (this._transitionHistory.length > this._maxHistorySize) {
        this._transitionHistory.shift()
      }

      const timeSinceStart = Date.now() - (this._transitionHistory[0]?.timestamp || Date.now())
      log(`[SessionState] ${currentState} -> ${newState}`, {
        context: context || {},
        transitionNumber: this._transitionHistory.length,
        timeSinceStart: `${Math.round(timeSinceStart / 1000)}s`
      })
      this._state.next(newState)
      this._onStateChange(newState, context)
    } else {
      warn(
        `[SessionState] Invalid transition ${currentState} -> ${newState}`,
        context
      )
    }
  }

  /**
   * Check if a transition is valid
   */
  private _isValidTransition(from: SessionState, to: SessionState): boolean {
    // Define valid transitions
    const validTransitions: Record<SessionState, SessionState[]> = {
      [SessionState.Unknown]: [
        SessionState.Authenticated,
        SessionState.LoggedOut
      ],
      [SessionState.Authenticated]: [
        SessionState.Refreshing,
        SessionState.Error,
        SessionState.Expired,
        SessionState.LoggedOut
      ],
      [SessionState.Refreshing]: [
        SessionState.Authenticated,
        SessionState.Error,
        SessionState.Expired,
        SessionState.LoggedOut
      ],
      [SessionState.Error]: [
        SessionState.Refreshing,
        SessionState.Authenticated,
        SessionState.Expired,
        SessionState.LoggedOut
      ],
      [SessionState.Expired]: [
        SessionState.Refreshing,
        SessionState.Authenticated,
        SessionState.LoggedOut
      ],
      [SessionState.LoggedOut]: [
        SessionState.Authenticated,
        SessionState.Unknown
      ]
    }

    const allowed = validTransitions[from] || []
    return allowed.includes(to)
  }

  /**
   * Handle side effects of state changes
   */
  private _onStateChange(state: SessionState, context?: any): void {
    switch (state) {
      case SessionState.Authenticated:
        // Session is healthy
        break

      case SessionState.Refreshing:
        // Token refresh in progress
        break

      case SessionState.Error:
        // Error occurred - context should contain error details
        error('[SessionState] Error state entered', context)
        break

      case SessionState.Expired:
        // Session expired - may need to refresh or logout
        warn('[SessionState] Session expired', context)
        break

      case SessionState.LoggedOut:
        // User logged out
        break
    }
  }

  /**
   * Reset state (useful for testing or cleanup)
   */
  reset(): void {
    this._state.next(SessionState.Unknown)
    this._transitionHistory = []
  }
}
